import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Privacy = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-8 text-codex-text">
            {t('Privacy Policy', 'Política de Privacidad')}
          </h1>

          <div className="prose-invert space-y-6 text-codex-text-muted text-sm leading-relaxed">
            <p className="text-xs text-codex-text-dim">
              {t('Last updated: March 30, 2026', 'Última actualización: 30 de marzo de 2026')}
            </p>

            {/* 1. Data Controller */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('1. Data Controller', '1. Responsable de los datos')}
              </h2>
              <p>
                {t(
                  'Pantrio AI (pantrio.dev), located in Interlomas, Estado de México, Mexico, is the data controller responsible for the treatment of your personal data in accordance with applicable data protection laws, including the Federal Law on Protection of Personal Data Held by Private Parties (LFPDPPP) of Mexico.',
                  'Pantrio AI (pantrio.dev), con domicilio en Interlomas, Estado de México, México, es el responsable del tratamiento de sus datos personales de conformidad con las leyes de protección de datos aplicables, incluyendo la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) de México.'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Email', 'Correo electrónico')}: <a href="mailto:hello@pantrio.dev" className="text-codex-green hover:underline">hello@pantrio.dev</a></li>
                <li>{t('Phone', 'Teléfono')}: <a href="tel:+524462421328" className="text-codex-green hover:underline">+52 (446) 242-1328</a></li>
                <li>{t('Location', 'Ubicación')}: Interlomas, Estado de México, México</li>
              </ul>
            </section>

            {/* 2. Data Collected */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('2. Personal Data Collected', '2. Datos personales que recopilamos')}
              </h2>
              <p>
                {t(
                  'We may collect the following personal data depending on how you interact with us:',
                  'Podemos recopilar los siguientes datos personales dependiendo de cómo interactúe con nosotros:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Full name', 'Nombre completo')}</li>
                <li>{t('Phone number (including WhatsApp number)', 'Número de teléfono (incluyendo número de WhatsApp)')}</li>
                <li>{t('Email address', 'Correo electrónico')}</li>
                <li>{t('Company name', 'Nombre de la empresa')}</li>
                <li>{t('Messages and content sent via WhatsApp', 'Mensajes y contenido enviado a través de WhatsApp')}</li>
                <li>{t('Website usage data (pages visited, interaction patterns)', 'Datos de uso del sitio web (páginas visitadas, patrones de interacción)')}</li>
                <li>{t('Cookies and local storage data', 'Cookies y datos de almacenamiento local')}</li>
                <li>{t('Any additional information you voluntarily provide through our contact forms, chatbot, or WhatsApp conversations', 'Cualquier información adicional que proporcione voluntariamente a través de nuestros formularios de contacto, chatbot o conversaciones de WhatsApp')}</li>
              </ul>
            </section>

            {/* 3. How We Use Your Data */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('3. How We Use Your Data', '3. Cómo utilizamos sus datos')}
              </h2>
              <p>
                {t(
                  'Your personal data is used for the following purposes:',
                  'Sus datos personales son utilizados para los siguientes fines:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Responding to your inquiries and requests', 'Responder a sus consultas y solicitudes')}</li>
                <li>{t('Providing our AI agents and automation services', 'Proveer nuestros servicios de agentes de IA y automatización')}</li>
                <li>{t('Sending notifications, updates, and relevant communications via WhatsApp', 'Enviar notificaciones, actualizaciones y comunicaciones relevantes a través de WhatsApp')}</li>
                <li>{t('Scheduling calls and meetings', 'Agendar llamadas y reuniones')}</li>
                <li>{t('Improving our services, website, and user experience', 'Mejorar nuestros servicios, sitio web y experiencia del usuario')}</li>
                <li>{t('Complying with legal obligations', 'Cumplir con obligaciones legales')}</li>
              </ul>
            </section>

            {/* 4. WhatsApp Business API & Third-Party Integrations */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('4. WhatsApp Business API & Third-Party Integrations', '4. WhatsApp Business API e integraciones de terceros')}
              </h2>
              <p>
                {t(
                  'We use the WhatsApp Business API, provided by Meta Platforms, Inc. (formerly Facebook), to communicate with you. When you send us a message via WhatsApp, your messages, phone number, and profile information are processed through Meta\'s platform in accordance with Meta\'s own privacy policy.',
                  'Utilizamos la WhatsApp Business API, proporcionada por Meta Platforms, Inc. (anteriormente Facebook), para comunicarnos con usted. Cuando nos envía un mensaje a través de WhatsApp, sus mensajes, número de teléfono e información de perfil son procesados a través de la plataforma de Meta de conformidad con la política de privacidad de Meta.'
                )}
              </p>
              <p className="mt-2">
                {t(
                  'Additionally, we may use the following third-party services to deliver and improve our services:',
                  'Adicionalmente, podemos utilizar los siguientes servicios de terceros para brindar y mejorar nuestros servicios:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Meta / WhatsApp Business Platform — for messaging and customer communication', 'Meta / WhatsApp Business Platform — para mensajería y comunicación con clientes')}</li>
                <li>{t('Cloud hosting and infrastructure providers — for secure data storage', 'Proveedores de alojamiento e infraestructura en la nube — para almacenamiento seguro de datos')}</li>
                <li>{t('Analytics tools — for understanding website usage patterns', 'Herramientas de analítica — para comprender patrones de uso del sitio web')}</li>
                <li>{t('AI service providers — for powering our AI agent solutions', 'Proveedores de servicios de IA — para potenciar nuestras soluciones de agentes de IA')}</li>
              </ul>
              <p className="mt-2">
                {t(
                  'We ensure that any third-party service we use complies with applicable data protection standards and only processes your data as necessary to provide our services.',
                  'Nos aseguramos de que cualquier servicio de terceros que utilicemos cumpla con los estándares de protección de datos aplicables y solo procese sus datos según sea necesario para brindar nuestros servicios.'
                )}
              </p>
            </section>

            {/* 5. Consent and Opt-In */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('5. Consent and Opt-In', '5. Consentimiento y aceptación')}
              </h2>
              <p>
                {t(
                  'By initiating a conversation with us through WhatsApp, you consent to the collection and processing of your data as described in this Privacy Policy. You also consent to receive messages from us via WhatsApp related to our services, including responses to your inquiries, service notifications, and relevant updates.',
                  'Al iniciar una conversación con nosotros a través de WhatsApp, usted otorga su consentimiento para la recopilación y procesamiento de sus datos como se describe en esta Política de Privacidad. También consiente recibir mensajes de nuestra parte a través de WhatsApp relacionados con nuestros servicios, incluyendo respuestas a sus consultas, notificaciones de servicio y actualizaciones relevantes.'
                )}
              </p>
              <p className="mt-2">
                {t(
                  'Similarly, by submitting information through our website forms or chatbot, you consent to the collection and use of the information provided.',
                  'De igual manera, al enviar información a través de los formularios de nuestro sitio web o chatbot, usted consiente la recopilación y el uso de la información proporcionada.'
                )}
              </p>
              <p className="mt-2">
                {t(
                  'You may withdraw your consent at any time by contacting us at hello@pantrio.dev or by sending "STOP" via WhatsApp.',
                  'Puede retirar su consentimiento en cualquier momento contactándonos en hello@pantrio.dev o enviando "DETENER" a través de WhatsApp.'
                )}
              </p>
            </section>

            {/* 6. Data Storage and Security */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('6. Data Storage and Security', '6. Almacenamiento y seguridad de datos')}
              </h2>
              <p>
                {t(
                  'We implement administrative, technical, and physical security measures to protect your personal data against damage, loss, alteration, destruction, or unauthorized use, access, or treatment. These measures include:',
                  'Implementamos medidas de seguridad administrativas, técnicas y físicas para proteger sus datos personales contra daño, pérdida, alteración, destrucción o uso, acceso o tratamiento no autorizado. Estas medidas incluyen:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Encryption of data in transit and at rest', 'Cifrado de datos en tránsito y en reposo')}</li>
                <li>{t('Restricted access controls to personal data', 'Controles de acceso restringido a datos personales')}</li>
                <li>{t('Secure cloud infrastructure with regular security audits', 'Infraestructura en la nube segura con auditorías de seguridad periódicas')}</li>
                <li>{t('Staff training on data protection best practices', 'Capacitación del personal en mejores prácticas de protección de datos')}</li>
              </ul>
            </section>

            {/* 7. Data Retention */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('7. Data Retention', '7. Retención de datos')}
              </h2>
              <p>
                {t(
                  'We retain your personal data only for as long as necessary to fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law. Specifically:',
                  'Conservamos sus datos personales solo durante el tiempo necesario para cumplir con los fines descritos en esta Política de Privacidad, a menos que un período de retención más largo sea requerido o permitido por la ley. Específicamente:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Contact information and inquiry records: up to 24 months after last interaction', 'Información de contacto y registros de consultas: hasta 24 meses después de la última interacción')}</li>
                <li>{t('WhatsApp conversation history: up to 12 months', 'Historial de conversaciones de WhatsApp: hasta 12 meses')}</li>
                <li>{t('Website usage data: up to 12 months', 'Datos de uso del sitio web: hasta 12 meses')}</li>
              </ul>
              <p className="mt-2">
                {t(
                  'After the retention period, your data will be securely deleted or anonymized.',
                  'Después del período de retención, sus datos serán eliminados de forma segura o anonimizados.'
                )}
              </p>
            </section>

            {/* 8. Your Rights */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('8. Your Rights (ARCO Rights)', '8. Sus derechos (Derechos ARCO)')}
              </h2>
              <p>
                {t(
                  'Under applicable law, you have the following rights regarding your personal data:',
                  'De conformidad con la legislación aplicable, usted tiene los siguientes derechos sobre sus datos personales:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>{t('Access', 'Acceso')}</strong>: {t('Request a copy of the personal data we hold about you', 'Solicitar una copia de los datos personales que tenemos sobre usted')}</li>
                <li><strong>{t('Rectification', 'Rectificación')}</strong>: {t('Request correction of inaccurate or incomplete data', 'Solicitar la corrección de datos inexactos o incompletos')}</li>
                <li><strong>{t('Cancellation', 'Cancelación')}</strong>: {t('Request deletion of your personal data', 'Solicitar la eliminación de sus datos personales')}</li>
                <li><strong>{t('Opposition', 'Oposición')}</strong>: {t('Object to the processing of your personal data for specific purposes', 'Oponerse al tratamiento de sus datos personales para fines específicos')}</li>
              </ul>
              <p className="mt-2">
                {t(
                  'To exercise any of these rights, please send an email to hello@pantrio.dev with the subject "ARCO Rights Request." We will respond to your request within 20 business days.',
                  'Para ejercer cualquiera de estos derechos, envíe un correo electrónico a hello@pantrio.dev con el asunto "Solicitud de derechos ARCO". Responderemos a su solicitud dentro de los 20 días hábiles siguientes.'
                )}
              </p>
            </section>

            {/* 9. Cookies and Analytics */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('9. Cookies and Analytics', '9. Cookies y analítica')}
              </h2>
              <p>
                {t(
                  'Our website may use cookies and similar technologies (such as localStorage) to enhance your browsing experience. We use localStorage to remember your language preference. We may also use analytics tools to understand how visitors interact with our website.',
                  'Nuestro sitio web puede utilizar cookies y tecnologías similares (como localStorage) para mejorar su experiencia de navegación. Utilizamos localStorage para recordar su preferencia de idioma. También podemos utilizar herramientas de analítica para comprender cómo los visitantes interactúan con nuestro sitio web.'
                )}
              </p>
              <p className="mt-2">
                {t(
                  'You can control cookies through your browser settings. Disabling cookies may affect certain features of our website.',
                  'Puede controlar las cookies a través de la configuración de su navegador. Desactivar las cookies puede afectar ciertas funciones de nuestro sitio web.'
                )}
              </p>
            </section>

            {/* 10. Changes to this Policy */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('10. Changes to this Privacy Policy', '10. Cambios a esta Política de Privacidad')}
              </h2>
              <p>
                {t(
                  'We reserve the right to update this Privacy Policy at any time. Any changes will be published on this page with the updated date. We recommend reviewing this page periodically. For significant changes, we may notify you through our website or via WhatsApp if you have an active conversation with us.',
                  'Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. Cualquier cambio será publicado en esta página con la fecha de actualización. Le recomendamos revisar esta página periódicamente. Para cambios significativos, podemos notificarle a través de nuestro sitio web o vía WhatsApp si tiene una conversación activa con nosotros.'
                )}
              </p>
            </section>

            {/* 11. Contact */}
            <section>
              <h2 className="text-lg font-semibold text-codex-text mb-3">
                {t('11. Contact', '11. Contacto')}
              </h2>
              <p>
                {t(
                  'If you have any questions, concerns, or requests regarding this Privacy Policy or the treatment of your personal data, please contact us at:',
                  'Si tiene alguna pregunta, inquietud o solicitud relacionada con esta Política de Privacidad o el tratamiento de sus datos personales, contáctenos en:'
                )}
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('Email', 'Correo electrónico')}: <a href="mailto:hello@pantrio.dev" className="text-codex-green hover:underline">hello@pantrio.dev</a></li>
                <li>{t('Phone', 'Teléfono')}: <a href="tel:+524462421328" className="text-codex-green hover:underline">+52 (446) 242-1328</a></li>
                <li>{t('Location', 'Ubicación')}: Interlomas, Estado de México, México</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
